import React, {ReactNode, useMemo} from 'react';

import {PrescriptionTable} from '@optimaxdev/design-system';
import {floatPriceFromFormatted} from '@optimaxdev/utils';

import {Price} from 'components/price/price';
import {SINGLE_VISION, SINGLE_VISION_USAGE_NAME, PRESCRIPTION_METHOD} from 'constants/product';
import {BaseItemPrice} from 'desktop/components/baseItemPrice/baseItemPrice';
import {PrescriptionType} from 'desktop/types/checkoutItemsType';
import {getPreparedCLPrescription} from 'desktop/utils/getPreparedCLPrescription/getPreparedCLPrescription';

import {PrescriptionUploaded} from '../prescriptionUploaded/prescriptionUploaded';

import s from './cartItemDetailsRowV2.scss';

export type PropsType = {
    title: ReactNode;
    label?: ReactNode;
    postfix?: ReactNode;
    price?: Maybe<number | string>;
    specialPrice?: Maybe<number | string>;
    description?: ReactNode;
    dataTestName?: Maybe<string>;
    dataTestValue?: Maybe<string>;
    showDiscount?: boolean;
    prescription?: PrescriptionType;
    forLenses?: boolean;
    prescriptionType?: number;
};

/**
 * Info block for item in cart
 * @param {PropsType} props - component props
 * @returns {React.ReactElement} - info block component
 */
export const CartItemDetailsRowV2 = ({
    dataTestName = null,
    dataTestValue = null,
    description = null,
    forLenses = false,
    label = null,
    postfix = null,
    prescription,
    prescriptionType,
    price = null,
    showDiscount = true,
    specialPrice = null,
    title = null,
}: PropsType): React.ReactElement => {
    const formattedPrice = floatPriceFromFormatted(String(price));
    const isSingleVision = title === SINGLE_VISION_USAGE_NAME || title === SINGLE_VISION;

    const showPrescriptionName = useMemo(
        () =>
            prescription?.name &&
            prescription.method !== PRESCRIPTION_METHOD.UPLOAD_METHOD &&
            !forLenses &&
            ` (${prescription?.name})`,
        [forLenses, prescription],
    );

    const storePrescriptionTypeDependComponent = (
        prescriptionOfItem: PrescriptionType,
    ): React.ReactNode => {
        const preparedOd = getPreparedCLPrescription(prescriptionOfItem, prescriptionType, 'od');
        const preparedOs = getPreparedCLPrescription(prescriptionOfItem, prescriptionType, 'os');

        switch (prescriptionOfItem.method) {
            case PRESCRIPTION_METHOD.UPLOAD_METHOD:
                return (
                    <div className={s.prescription}>
                        <PrescriptionUploaded prescription={prescriptionOfItem} />
                    </div>
                );
            case PRESCRIPTION_METHOD.FILL_IT_ONLINE_METHOD:
                return (
                    <div className={s.prescription}>
                        <PrescriptionTable
                            pd={
                                isSingleVision
                                    ? {...prescriptionOfItem.pd, near: 'None'}
                                    : prescriptionOfItem.pd
                            }
                            ariaLabel="prescription table"
                            isContactLenses={forLenses}
                            od={preparedOd}
                            os={preparedOs}
                            sizeDesktop="small"
                            variant="gray"
                            shortTitles
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className={s.row} data-test-name={dataTestName} data-test-value={dataTestValue}>
            {label && <span className={s.label}>{label}</span>}
            <span className={s.title}>{title}</span>
            {prescription?.method === PRESCRIPTION_METHOD.SEND_EMAIL_OR_FAX_METHOD && (
                <span className={s.sendPrescription}>(Send Prescription Later)</span>
            )}
            {formattedPrice > 0 && (
                <span className={s.price}>
                    (
                    {showDiscount ? (
                        <BaseItemPrice price={formattedPrice} specialPrice={specialPrice} />
                    ) : (
                        <Price>{specialPrice}</Price>
                    )}
                    )
                </span>
            )}
            {postfix}
            {showPrescriptionName}
            {prescription && storePrescriptionTypeDependComponent(prescription)}

            {description && <div>{description}</div>}
        </div>
    );
};
